/* eslint-disable jsx-a11y/media-has-caption */
import React, {
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import 'videojs-seek-buttons';
import 'videojs-vjsdownload';

import 'video.js/dist/video-js.css';
import 'videojs-vjsdownload/dist/videojs-vjsdownload.css';
import 'videojs-seek-buttons/dist/videojs-seek-buttons.css';
import './styles.css';
import '../video-marker-unmoderated/index';
import '../video-marker-unmoderated/styles.css';

import {
  useMedia, Media, MediaContext, withMedia,
} from '@playbookux/playbook-web-core';
import { OverlayPlayer } from './styles';

export { withMedia, useMedia, Media };

const VideoPlayerWithOverlay = ({
  screenRecordingSrc,
  faceRecordingSrc,
  downloadLink,
  children,
  markers,
}) => {
  const defaultOptions = {
    id: 'video-player',
    playbackRates: [1.0, 1.5, 2.0],
    plugins: {
      seekButtons: {
        forward: 10,
        back: 10,
      },
      vjsdownload: {
        beforeElement: 'playbackRateMenuButton',
        textControl: 'Download video',
        name: 'downloadButton',
        downloadURL: downloadLink,
      },
      markers: {
        ...markers,
      },
    },
    controls: true,
    autoplay: false,
    fill: true,
    fluid: false,
    preload: 'auto',
    html5: {
      hls: {
        enableLowInitialPlaylist: true,
        smoothQualityChange: true,
        overrideNative: false,
      },
      vhs: {
        overrideNative: false,
      },
    },
    clipButton: {
      beforeElement: 'downloadButton',
      textControl: 'Create clip',
      name: 'clipButton',
    },
  };

  const overlayOptions = {
    id: 'face-recording-player',
    controls: false,
    autoplay: false,
    muted: true,
    fill: true,
    fluid: false,
    preload: 'auto',
    html5: {
      hls: {
        enableLowInitialPlaylist: true,
        smoothQualityChange: true,
        overrideNative: false,
      },
      vhs: {
        overrideNative: false,
      },
    },
  };

  const {
    player,
    setPlayer,
    setPlayerState,
    setPlayerSetters,
  } = useContext(MediaContext);

  const videoRef = useRef(null);
  const overlayContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const overlayPlayerRef = useRef(null);
  const overlayResizableRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [playerReady, setPlayerReady] = useState(false);
  const [overlayPlayerReady, setOverlayPlayerReady] = useState(false);
  const [loadedData, setLoadedData] = useState(false);
  const [playerRect, setPlayerRect] = useState({ height: 0, width: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [positionRelative, setPositionRelative] = useState({ x: 0, y: 0 });
  const [coordinate, setCoordinate] = useState({ x: 0, y: 0 });
  const headerHeight = 54;

  useEffect(() => {
    const vjsPlayer = videojs(videoRef.current, {
      ...defaultOptions,
      inactivityTimeout: 0,
      sources: [screenRecordingSrc],
    });

    const vjsOverlayPlayer = videojs(overlayRef.current, {
      ...overlayOptions,
      inactivityTimeout: 0,
      sources: [faceRecordingSrc],
    });

    setPlayer(vjsPlayer);

    setPlayerSetters({
      seekTo: (time) => vjsPlayer.currentTime(time),
      pause: () => {
        vjsPlayer.pause();
      },
      play: () => {
        vjsPlayer.play();
      },
      getDuration: () => vjsPlayer.duration(),
    });

    vjsPlayer.on('loadedmetadata', () => {
      vjsPlayer.hasStarted(true);
      vjsOverlayPlayer.hasStarted(true);
    });

    vjsPlayer.on('loadeddata', () => {
      vjsPlayer.hasStarted(true);
      setLoadedData(true);
    });

    vjsPlayer.on('timeupdate', () => {
      setCurrentTime(vjsPlayer.currentTime());
    });

    vjsPlayer.on('playerresize', () => {
      const ctrlBar = vjsPlayer.el().getBoundingClientRect();
      setPlayerRect({ height: ctrlBar.height, width: ctrlBar.width });
    });

    vjsPlayer.on('ready', () => {
      setPlayerReady(true);
    });

    vjsOverlayPlayer.on('ready', () => {
      setOverlayPlayerReady(true);
    });

    vjsOverlayPlayer.on('play', () => {
      vjsPlayer.play();

      if (vjsOverlayPlayer.currentTime() !== vjsPlayer.currentTime()) {
        vjsOverlayPlayer.currentTime(vjsPlayer.currentTime());
      }
    });

    vjsPlayer.on('play', () => {
      if (vjsOverlayPlayer.currentTime() !== vjsPlayer.currentTime()) {
        vjsOverlayPlayer.currentTime(vjsPlayer.currentTime());
      }

      // This interval is necessary to avoid a racing condition
      // between the play and pause events
      const intervalId = setInterval(() => {
        if (vjsPlayer.readyState() > 2 && vjsOverlayPlayer.readyState() > 2) {
          vjsOverlayPlayer.play();
          clearInterval(intervalId);
        }
      }, 50);
    });

    vjsPlayer.on('seeked', () => {
      // we need to sync the overlay player with the main player
      if (vjsOverlayPlayer.currentTime() !== vjsPlayer.currentTime()) {
        vjsOverlayPlayer.currentTime(vjsPlayer.currentTime());
      }
    });

    vjsPlayer.on('pause', () => {
      vjsOverlayPlayer.pause();
    });

    vjsOverlayPlayer.on('pause', () => {
      vjsPlayer.pause();
    });

    vjsPlayer.on('volumechange', () => {
      vjsOverlayPlayer.volume(vjsPlayer.volume());

      // It doesn't have a specific event for mute/unmute
      // so we check at volumechange event for
      if (vjsPlayer.muted()) {
        vjsOverlayPlayer.muted(true);
      } else {
        vjsOverlayPlayer.muted(false);
      }
    });

    return () => {
      if (player !== null) {
        player.markers.destroy();
        player.dispose();
      }
    };
  }, []);

  useEffect(() => {
    setPlayerState({
      currentTime,
      playerReady,
      loadedData,
      playerRect,
    });
  }, [currentTime, loadedData, playerReady, playerRect, setPlayerState]);

  useEffect(() => {
    if (player !== null) {
      player.src({ screenRecordingSrc });
    }
  }, [screenRecordingSrc]);

  function minimizeOverlay() {
    const video = overlayPlayerRef.current;
    const { resizable } = overlayResizableRef.current;

    video.classList.toggle('minimized');
    resizable.classList.toggle('minimized');
  }

  function handleFullscreen() {
    const video = overlayPlayerRef.current;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen(); /* Firefox */
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen(); /* Chrome, Safari and Opera */
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen(); /* IE/Edge */
    }
  }

  // Prevent drop the controller outside the video screen
  const handleLimitMovement = (xValue, yValue) => {
    let x = 0;
    let y = 0;
    const { innerHeight, innerWidth } = window;
    const currentVideoContainerWidth = overlayContainerRef.current.offsetWidth;
    const currentVideoContainerHeight = overlayContainerRef.current.offsetHeight;

    if (xValue >= 0) {
      if ((xValue + currentVideoContainerWidth) > innerWidth) {
        x = innerWidth - currentVideoContainerWidth;
      } else {
        x = xValue;
      }
    }

    if (yValue >= 0) {
      if ((yValue + currentVideoContainerHeight + headerHeight) > innerHeight) {
        y = innerHeight - currentVideoContainerHeight - headerHeight - 2; // offset to avoid creating a scroll bar
      } else {
        y = yValue;
      }
    } else {
      y = 0;
    }

    return { x, y };
  };

  const onMouseDown = (event) => {
    const isVideoElement = event.target.className.includes('vjs-tech');
    const isVideoHeaderElement = event.target.className.includes('overlay-actions-wrapper');

    // only left mouse button and video element
    if (event.button !== 0 || (!isVideoElement && !isVideoHeaderElement)) return;

    if (overlayContainerRef) {
      setIsDragging(true);
      setPositionRelative({
        x: event.pageX - overlayContainerRef.current.offsetLeft,
        y: event.pageY - overlayContainerRef.current.offsetTop,
      });
    }
    event.stopPropagation();
    event.preventDefault();
  };

  const onMouseMove = (event) => {
    if (!isDragging) return;

    const xValue = event.pageX - positionRelative.x;
    const yValue = event.pageY - positionRelative.y;
    const { x, y } = handleLimitMovement(xValue, yValue);

    setCoordinate({ x, y });

    event.stopPropagation();
    event.preventDefault();
  };

  const onMouseUp = (event) => {
    setIsDragging(false);
    event.stopPropagation();
    event.preventDefault();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    }
  }, [isDragging]);

  return (
    <>
      <div data-vjs-player>
        <video ref={videoRef} className="video-js" />
        {(playerReady && overlayPlayerReady) && children}
      </div>

      <OverlayPlayer.Container
        className="overlay-player"
        onMouseDown={onMouseDown}
        coordinate={coordinate}
        ref={overlayContainerRef}
      >
        <OverlayPlayer.ActionsWrapper className="overlay-actions-wrapper">
          <OverlayPlayer.ActionButton type="button" onClick={() => minimizeOverlay()}>
            <OverlayPlayer.Icon name="window minimize" />
          </OverlayPlayer.ActionButton>

          <OverlayPlayer.ActionButton type="button" onClick={() => handleFullscreen()}>
            <OverlayPlayer.Icon name="window maximize outline" />
          </OverlayPlayer.ActionButton>
        </OverlayPlayer.ActionsWrapper>

        <OverlayPlayer.Resizable
          defaultSize={{
            width: 334,
            height: 210,
          }}
          minHeight={210}
          maxHeight={420}
          minWidth={334}
          maxWidth={668}
          lockAspectRatio
          bounds="window"
          ref={overlayResizableRef}
          className="overlay-resizable"
        >
          <OverlayPlayer data-vjs-player ref={overlayPlayerRef}>
            <OverlayPlayer.Video
              ref={overlayRef}
              className="video-js-overlay"
            />
          </OverlayPlayer>
        </OverlayPlayer.Resizable>
      </OverlayPlayer.Container>
    </>
  );
};

VideoPlayerWithOverlay.propTypes = {
  screenRecordingSrc: PropTypes.string.isRequired,
  faceRecordingSrc: PropTypes.string.isRequired,
  downloadLink: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  markers: PropTypes.objectOf(PropTypes.shape({})),
};

VideoPlayerWithOverlay.defaultProps = {
  children: null,
  markers: {},
};

export default VideoPlayerWithOverlay;
